/**
 * Air Quality Polling Hook
 * Fetches real-time air quality data and updates the store
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { fetchAirQuality, fetchAirQualityForecast } from '@/services/api/airQualityApi';

interface UseAirQualityPollingOptions {
  /** Polling interval in milliseconds (default: 5 minutes) */
  interval?: number;
  /** Whether to start polling immediately (default: true) */
  enabled?: boolean;
}

export function useAirQualityPolling(options: UseAirQualityPollingOptions = {}) {
  const { interval = 5 * 60 * 1000, enabled = true } = options;

  const setAirQuality = useAirQualityStore((state) => state.setAirQuality);
  const setForecast = useAirQualityStore((state) => state.setForecast);
  const setLoading = useAirQualityStore((state) => state.setLoading);
  const setError = useAirQualityStore((state) => state.setError);

  const currentPosition = useLocationStore((state) => state.currentPosition);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!currentPosition || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const { lat, lng } = currentPosition;

      // Fetch air quality and forecast in parallel
      const [airQualityData, forecastData] = await Promise.all([
        fetchAirQuality(lat, lng),
        fetchAirQualityForecast(lat, lng),
      ]);

      if (airQualityData) {
        setAirQuality(airQualityData);
      } else {
        throw new Error('Failed to fetch air quality data');
      }

      if (forecastData) {
        setForecast(forecastData);
      }
    } catch (error) {
      console.error('Air quality fetch error:', error);
      setError(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [currentPosition, setAirQuality, setForecast, setLoading, setError]);

  // Initial fetch when position becomes available
  useEffect(() => {
    if (enabled && currentPosition) {
      fetchData();
    }
  }, [enabled, currentPosition, fetchData]);

  // Set up polling interval
  useEffect(() => {
    if (!enabled || !currentPosition) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(fetchData, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, currentPosition, interval, fetchData]);

  return {
    refetch: fetchData,
  };
}

export default useAirQualityPolling;
